/**
 * The project board, from the tab: the address it lives at, and the small
 * client for the two routes behind it (worker/projects.ts).
 *
 * **Retired 2026-10-18.** `/projects` and every address under it redirected
 * to the paid task board, `/tasks`, until that retired on 2026-10-31; both now
 * land on the Blobor showcase (lib/retired-tasks.ts, main.tsx), and the
 * screens that called the client below are gone. `isProjectsPath` is what the
 * redirect reads; the rest stays, with its tests, because the Worker routes
 * and the tables it reads stay too (docs/projects.md).
 *
 * The briefs are not fetched. They are a typed constant in the engine package
 * and they are already in this bundle, which is what lets `/projects` render
 * completely for somebody who is signed out, has never opened the course, and
 * is reading it on a phone from a link in a message — no request, no session,
 * no Worker invocation, and no virtual machine.
 *
 * What this module fetches is only the part that is a fact about an account:
 * which teams you are in, and which of them have entered. Signed out there is
 * nothing to ask for and nothing is asked.
 *
 * ## The address is a path, not a query
 *
 * `/projects`, for the reason `/u/<tag>` is a path (lib/person.ts): this is a
 * link that gets pasted into a message and posted somewhere, and a path is
 * what a page looks like everywhere else on the web. The asset store already
 * serves `index.html` for any path that is not a file
 * (`not_found_handling = "single-page-application"` in wrangler.toml), so it
 * costs no routing and no zone configuration to have — which matters here,
 * because the deploy token cannot configure a zone at all.
 *
 * That same fallback is what makes `/projects/<slug>` free: one brief in full
 * is a second address under the same asset, with no route to add, no Worker
 * invocation and nothing in wrangler.toml to change. A brief is the thing
 * somebody links to — "look at this one" — and a fragment on a scrolling
 * document was never that.
 */
import type { TeamProgress } from '@engineer-os/engine';
import { chatUrl } from './chat';

/** One team this account is in, as the board needs it. */
export interface BoardTeam {
  id: string;
  name: string;
  role: 'lead' | 'member';
  size: number;
  /** How many of those members have confirmed the whole course. A count and
   *  never a roster: which teammate is short is their fact, not the lead's. */
  finished: number;
}

/** One entry made by a team this account is in. */
export interface BoardApplication {
  project: string;
  teamId: string;
  appliedAt: number;
  state: 'open' | 'withdrawn';
}

/** Everything the board knows about the reader. Empty is a valid answer and is
 *  what a signed-out reader gets, so no screen has to branch on null. */
export interface Board {
  /**
   * Whether the Worker recognised a session.
   *
   * Carried because "signed out" and "signed in with no team yet" are the same
   * empty board and want opposite sentences — one says sign in, the other says
   * go and make a team. The route answers 404 without a session, so a 200 is
   * the only thing that sets this true.
   *
   * Everything else that can go wrong — no database, an old deploy, no network
   * — reads as false and draws "sign in". That is the right way round: it
   * offers a reader something to do, where a wrong "make a team" would send
   * somebody to a panel that will not help them.
   */
  signedIn: boolean;
  /**
   * How many checkpoints this reader has confirmed, and how many the board
   * asks for.
   *
   * Both come from the server rather than being counted in the tab, for the
   * reason the gate exists at all: the tab's own record of what it has cleared
   * is a claim, and the requirement here is about what a container graded. The
   * denominator is sent too, so a Worker that has added a checkpoint does not
   * need this bundle redeployed before the page stops saying the wrong total.
   */
  confirmed: number;
  required: number;
  teams: BoardTeam[];
  applications: BoardApplication[];
  /**
   * How many teams have a standing entry, by slug.
   *
   * The one number on the board that is about everybody rather than about the
   * reader, and the reason it lives here rather than in the engine's constant:
   * it is live, so it cannot be a thing a bundle ships. A slug missing from
   * this map is drawn with no count at all — deliberately, because "0 teams
   * entered" invented by a read that failed is a claim about how popular a
   * brief is, made up by a browser.
   *
   * Signed out there is no board and therefore no counts, which is the same
   * absence and draws the same nothing.
   */
  entrants: Record<string, number>;
}

export const EMPTY_BOARD: Board = {
  signedIn: false,
  confirmed: 0,
  required: 0,
  teams: [],
  applications: [],
  entrants: {},
};

/* ── The field on one brief ─────────────────────────────────────────────── */

/**
 * One team on a brief, as every signed-in reader of it may see them.
 *
 * Carrying names rather than a count is a reversal, and a deliberate one: the
 * board used to answer "six teams entered" and refuse the list, because who
 * your competition is was nobody's business. That was right while nothing was
 * at stake. With one payment going to the first complete delivery, a reader
 * deciding whether to spend three weeks on a brief two teams have already
 * delivered is entitled to know it before they start.
 *
 * What is never here is the work: no link and no notes for anybody but the
 * team that filed them (`Delivery`, below).
 */
export interface FieldTeam {
  teamId: string;
  name: string;
  size: number;
  /** Self-reported and never checked. Every surface that draws this says so. */
  progress: TeamProgress;
  joinedAt: number;
  /** When their first live delivery landed, or null. */
  submittedAt: number | null;
  submissions: number;
}

/** One delivery of the reader's own team — the half nobody else sees. */
export interface Delivery {
  teamId: string;
  at: number;
  url: string;
  notes: string;
  met: string;
}

/** Who is on a brief, and what this reader's own teams have delivered. */
export interface Field {
  teams: FieldTeam[];
  mine: Delivery[];
}

export const EMPTY_FIELD: Field = { teams: [], mine: [] };

/**
 * The field for one brief.
 *
 * Its own route rather than part of the board, and asked for only by the page
 * that draws it: `/api/projects` answers "what is true about me" on every load
 * of the board, and nine readers in ten never open a brief. Every failure
 * collapses to empty for `fetchBoard`'s reason — the screen does the same
 * thing for a signed-out reader, an old deploy and no network.
 */
export async function fetchField(slug: string): Promise<Field> {
  try {
    const r = await fetch(chatUrl(`/api/projects/${encodeURIComponent(slug)}/field`).href, {
      credentials: 'include',
      cache: 'no-store',
    });
    if (!r.ok) return EMPTY_FIELD;
    return readField(await r.json());
  } catch {
    return EMPTY_FIELD;
  }
}

/** What a delivery attempt can tell the interface apart. */
export type DeliveryOutcome =
  | { ok: true; at: number }
  | { ok: false; reason: 'notEntered' | 'badUrl' | 'tooLong' | 'notLead' | 'other' };

/** File a delivery: a link, a note, and the team's own answer to `done`. */
export async function submitDelivery(
  slug: string,
  teamId: string,
  url: string,
  notes: string,
  met: string,
): Promise<DeliveryOutcome> {
  return postDelivery(`/api/projects/${encodeURIComponent(slug)}/submit`, { teamId, url, notes, met });
}

/** Take every live delivery this team has on the brief back out of the field. */
export async function withdrawDelivery(slug: string, teamId: string): Promise<DeliveryOutcome> {
  return postDelivery(`/api/projects/${encodeURIComponent(slug)}/unsubmit`, { teamId });
}

/** Say where the team has got to. A claim, and drawn as one. */
export async function setTeamProgress(slug: string, teamId: string, progress: TeamProgress): Promise<DeliveryOutcome> {
  return postDelivery(`/api/projects/${encodeURIComponent(slug)}/progress`, { teamId, progress });
}

async function postDelivery(path: string, body: Record<string, unknown>): Promise<DeliveryOutcome> {
  try {
    const r = await fetch(chatUrl(path).href, {
      method: 'POST',
      credentials: 'include',
      // Load-bearing, not a formality: half of what stops a cross-site form
      // reaching this route, the session cookie's SameSite=Lax being the other.
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = (await r.json().catch(() => null)) as { at?: unknown; error?: unknown } | null;
    if (r.ok) return { ok: true, at: typeof data?.at === 'number' ? data.at : Date.now() };
    const error = typeof data?.error === 'string' ? data.error : '';
    if (error === 'not entered') return { ok: false, reason: 'notEntered' };
    if (error === 'bad url') return { ok: false, reason: 'badUrl' };
    if (error === 'url too long' || error === 'notes too long') return { ok: false, reason: 'tooLong' };
    if (error === 'not the lead') return { ok: false, reason: 'notLead' };
    return { ok: false, reason: 'other' };
  } catch {
    return { ok: false, reason: 'other' };
  }
}

/**
 * Checked rather than trusted, one row at a time, for `readBoard`'s reason:
 * this is JSON from a Worker that may be a deploy either side of this bundle,
 * and one malformed team must not empty a field.
 *
 * Exported as `readFieldForTest` below rather than tested through `fetch`:
 * what is worth pinning here is the validation, and the two rules it enforces
 * — a link that is not http(s) is dropped, and an unknown progress word falls
 * to the quietest one — are the two that would otherwise be discovered by a
 * reader looking at a page that had believed a Worker.
 */
function readField(raw: unknown): Field {
  if (!raw || typeof raw !== 'object') return EMPTY_FIELD;
  const value = raw as { teams?: unknown; mine?: unknown };
  return {
    teams: Array.isArray(value.teams) ? value.teams.map(readFieldTeam).filter(isPresent) : [],
    mine: Array.isArray(value.mine) ? value.mine.map(readDelivery).filter(isPresent) : [],
  };
}

function readFieldTeam(raw: unknown): FieldTeam | null {
  if (!raw || typeof raw !== 'object') return null;
  const t = raw as Record<string, unknown>;
  if (typeof t.teamId !== 'string' || !t.teamId) return null;
  if (typeof t.name !== 'string' || !t.name) return null;
  return {
    teamId: t.teamId,
    name: t.name,
    size: typeof t.size === 'number' && t.size > 0 ? t.size : 0,
    // Anything this bundle does not recognise reads as 'joined': a word the
    // page cannot draw would otherwise leave a team with no state at all.
    progress: t.progress === 'building' || t.progress === 'stopped' ? t.progress : 'joined',
    joinedAt: typeof t.joinedAt === 'number' ? t.joinedAt : 0,
    submittedAt: typeof t.submittedAt === 'number' && t.submittedAt > 0 ? t.submittedAt : null,
    submissions: typeof t.submissions === 'number' && t.submissions > 0 ? t.submissions : 0,
  };
}

function readDelivery(raw: unknown): Delivery | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  if (typeof d.teamId !== 'string' || !d.teamId) return null;
  // A link that is not `http(s)` is dropped rather than rendered inert: this
  // value becomes an anchor, and the only safe thing to do with a scheme this
  // page did not expect is to not draw it.
  if (typeof d.url !== 'string' || !/^https?:\/\//i.test(d.url)) return null;
  return {
    teamId: d.teamId,
    at: typeof d.at === 'number' ? d.at : 0,
    url: d.url,
    notes: typeof d.notes === 'string' ? d.notes : '',
    met: typeof d.met === 'string' ? d.met : '',
  };
}

/** Is this the retired board's address, or one of its briefs'? Every one of
 *  them goes to the Blobor showcase (lib/retired-tasks.ts). */
export function isProjectsPath(pathname: string): boolean {
  return /^\/projects(\/.*)?$/.test(pathname);
}

/**
 * The brief being asked for, or null for the board itself.
 *
 * Only the first segment is read, and it is not checked against the catalogue
 * here — `projectBySlug` does that and answers null, which the page draws as
 * the board. So `/projects/nonsense` and `/projects/status-page/notes` are a
 * board and a brief respectively rather than two different crashes, and a link
 * that rots in somebody's message lands somewhere useful.
 */
export function projectSlugFromPath(pathname: string): string | null {
  const match = /^\/projects\/([^/]+)/.exec(pathname);
  return match ? decodeURIComponent(match[1]!) : null;
}

/**
 * The reader's teams and entries, or an empty board.
 *
 * Every failure collapses to empty for the reason lib/person.ts gives about
 * null: the screen does the same thing for all of them. A signed-out reader, a
 * Worker with no database, an old deploy that has never heard of this route
 * and no network are four different facts and one interface — the briefs are
 * drawn and the entry controls are not.
 */
export async function fetchBoard(): Promise<Board> {
  try {
    const r = await fetch(chatUrl('/api/projects').href, { credentials: 'include', cache: 'no-store' });
    if (!r.ok) return EMPTY_BOARD;
    return readBoard(await r.json());
  } catch {
    return EMPTY_BOARD;
  }
}

/** What `POST .../apply` and `.../withdraw` can tell the interface apart. */
export type EntryOutcome =
  | { ok: true; appliedAt: number }
  | { ok: false; reason: 'closed' | 'already' | 'size' | 'other' }
  /** The gate: how many of the team have not finished the course. */
  | { ok: false; reason: 'unfinished'; unfinished: number };

/** Enter a team for a project. */
export async function enterProject(slug: string, teamId: string, pitch: string): Promise<EntryOutcome> {
  return post(`/api/projects/${slug}/apply`, { teamId, pitch });
}

/** Take a team back out. */
export async function withdrawProject(slug: string, teamId: string): Promise<EntryOutcome> {
  return post(`/api/projects/${slug}/withdraw`, { teamId });
}

async function post(path: string, body: Record<string, unknown>): Promise<EntryOutcome> {
  try {
    const r = await fetch(chatUrl(path).href, {
      method: 'POST',
      credentials: 'include',
      // The JSON content type is load-bearing and not a formality: it is half
      // of what stops a cross-site form posting this route, the session
      // cookie's SameSite=Lax being the other half.
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = (await r.json().catch(() => null)) as
      | { appliedAt?: unknown; error?: unknown; unfinished?: unknown }
      | null;
    if (r.ok) return { ok: true, appliedAt: typeof data?.appliedAt === 'number' ? data.appliedAt : Date.now() };
    const error = typeof data?.error === 'string' ? data.error : '';
    if (error === 'closed') return { ok: false, reason: 'closed' };
    if (error === 'already applied') return { ok: false, reason: 'already' };
    if (error === 'team size') return { ok: false, reason: 'size' };
    if (error === 'unfinished') {
      return { ok: false, reason: 'unfinished', unfinished: typeof data?.unfinished === 'number' ? data.unfinished : 1 };
    }
    return { ok: false, reason: 'other' };
  } catch {
    return { ok: false, reason: 'other' };
  }
}

/**
 * What came back, checked rather than trusted.
 *
 * whois.ts's rule again: this is JSON from a Worker that may be a deploy ahead
 * of or behind this bundle. A row that fails validation takes only itself out,
 * so one malformed entry cannot empty a board.
 */
function readBoard(raw: unknown): Board {
  if (!raw || typeof raw !== 'object') return EMPTY_BOARD;
  const value = raw as {
    teams?: unknown;
    applications?: unknown;
    confirmed?: unknown;
    required?: unknown;
    entrants?: unknown;
  };
  const teams = Array.isArray(value.teams) ? value.teams.map(readTeam).filter(isPresent) : [];
  const applications = Array.isArray(value.applications)
    ? value.applications.map(readApplication).filter(isPresent)
    : [];
  // Only a 200 reaches here, and only a session gets one. A `required` that
  // failed validation reads as 0, which makes the gate below draw nothing
  // rather than "0 of 0" — an old Worker should cost the reader a control, not
  // a wrong sentence about themselves.
  return {
    signedIn: true,
    confirmed: typeof value.confirmed === 'number' && value.confirmed >= 0 ? value.confirmed : 0,
    required: typeof value.required === 'number' && value.required > 0 ? value.required : 0,
    teams,
    applications,
    entrants: readEntrants(value.entrants),
  };
}

/**
 * The per-slug counts, with every entry checked one at a time.
 *
 * A count that is not a whole number at or above zero is dropped rather than
 * clamped, and dropping it draws no line at all. That is the right failure for
 * this particular number: it is the only thing on the board that talks about
 * other people, so an unreadable one should disappear rather than become a
 * zero somebody reads as "nobody wanted this".
 */
function readEntrants(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, number> = {};
  for (const [slug, count] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof count === 'number' && Number.isInteger(count) && count >= 0) out[slug] = count;
  }
  return out;
}

function readTeam(raw: unknown): BoardTeam | null {
  if (!raw || typeof raw !== 'object') return null;
  const t = raw as Record<string, unknown>;
  if (typeof t.id !== 'string' || !t.id) return null;
  if (typeof t.name !== 'string' || !t.name) return null;
  return {
    id: t.id,
    name: t.name,
    role: t.role === 'lead' ? 'lead' : 'member',
    size: typeof t.size === 'number' && t.size > 0 ? t.size : 0,
    // Absent or malformed reads as nobody finished, which closes the control
    // rather than opening it. A validation failure must not be a way in.
    finished: typeof t.finished === 'number' && t.finished > 0 ? t.finished : 0,
  };
}

function readApplication(raw: unknown): BoardApplication | null {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;
  if (typeof a.project !== 'string' || !a.project) return null;
  if (typeof a.teamId !== 'string' || !a.teamId) return null;
  return {
    project: a.project,
    teamId: a.teamId,
    appliedAt: typeof a.appliedAt === 'number' ? a.appliedAt : 0,
    // Anything this bundle does not recognise reads as withdrawn rather than
    // open: a state we cannot draw is one we should not draw as a commitment.
    state: a.state === 'open' ? 'open' : 'withdrawn',
  };
}

function isPresent<T>(value: T | null): value is T {
  return value !== null;
}

/** The field validator, for web/test/projects.test.ts. Not used by the app,
 *  which reaches it through `fetchField`. */
export const readFieldForTest = readField;
